import { ComboBoxOption, ComboBoxValues } from "../ComboBoxComponent";

/**
 * Najde v poli options položku multieditace, v opačném případě vrací undefined.
 * @param options položky comboboxu
 */
export const getMultiEditOption = (options: ReadonlyArray<ComboBoxOption>): ComboBoxOption | undefined => options.find(op => op.value === ComboBoxValues.multiEditOptionValue);

/**
 * Vrací, jestli se hodnota comboboxu označí jako prázdná.
 * @param value hodnota comboboxu
 */
export const isEmptyValue = (value?: string | null): boolean => [ComboBoxValues.emptyOptionValue, ComboBoxValues.placeholderOptionValue, "", null, undefined].includes(value);

/**
 * Vrací, zda je hodnota obsažena v poli nedefaultních možností. Defaultní možnosti jsou např. placeholder, prázdná možnost nebo možnost multieditace.
 * Všechny zbývající jsou nedefaultní a mají value >= 0.
 * @param options všechny možnosti comboboxu
 * @param value hodnota comboboxu
 */
export const nonDefaultOptionsContain = (options: ReadonlyArray<ComboBoxOption>, value?: string | null): boolean => options.some(op => op.value >= 0 && op.value.toString() === value?.toString());

/**
 * Zjistí, zda je příchozí hodnota prázdná a v takovém případě vrátí `placeholderOptionValue`.
 * @param rawValue původní hodnota k zobrazení v comboboxu
 */
export const updateEmptyOptionValueIfNeeded = (rawValue?: string): string | undefined => isEmptyValue(rawValue) ? ComboBoxValues.placeholderOptionValue : rawValue;

/**
 * Upraví hodnotu comboboxu v režimu multieditace.
 * Rozlišuje, zda jsme v rámci multieditace v editaci a zda je hodnota povinná a na tomto základě hodnotu upraví/nechá stejnou.
 * @param options položky comboboxu
 * @param rawValue původní hodnota k zobrazení v comboboxu
 * @param isEditing příznak, zda je aktivní editace
 * @param required příznak, zda je hodnota povinná
 */
export const updateValueInMultiEditing = (options: ReadonlyArray<ComboBoxOption>, rawValue?: string, isEditing?: boolean, required?: boolean): string | undefined => {
    const rawStringValue = rawValue?.toString();

    // Pokud je zapnutá multieditace s editací, nastavuje hodnotu z rawValue, protože je v tomto režimu možné měnit hodnotu z comboboxu.
    if (isEditing) {
        // Pokud je required, tak není potřeba měnit prázdnou hodnotu na placeholder.
        if (required) {
            return rawStringValue;
        }

        // Pokud není required, je nutné změnit prázdnou hodnotu na placeholder.
        return updateEmptyOptionValueIfNeeded(rawStringValue);
    }

    // Pokud je zapnutá multieditace bez editace, zobrazí popisek pro multieditaci.
    return getMultiEditOption(options)?.value.toString();
};

/**
 * Pokud není nastaven režim editace, vrací všechny možnosti bez ohledu na `hide` atribut dané možnosti.
 * Pokud je nastaven režim editace, odfiltruje všechny možnosti s atributem `hide=true` vyjma placeholder možnosti.
 * @param options možnosti comboboxu
 * @param isEditing příznak, zda je aktivní editace
 */
export const removeHidden = (options: ReadonlyArray<ComboBoxOption>, isEditing?: boolean): ReadonlyArray<ComboBoxOption> => isEditing ?
    options.filter(option => !option.hide || option.isPlaceholder) : options;

/**
 * Vrací, zda je `value` prázdná hodnota nebo ne.
 * @param options možnosti comboboxu
 * @param value hodnota comboboxu
 * @param multiEditActive příznak, zda je aktivní multieditace
 */
export const isEmpty = (options: ReadonlyArray<ComboBoxOption>, value?: string, multiEditActive?: boolean): boolean => {
    const isPlaceholderOrHidden = value === ComboBoxValues.placeholderOptionValue || !nonDefaultOptionsContain(options, value);
    if (multiEditActive) {
        return value !== ComboBoxValues.multiEditOptionValue && isPlaceholderOrHidden;
    }

    return isPlaceholderOrHidden;
};
